<template>
    <div>
        <label for="manufacturer">
            <span class="headline" v-text="lang.manufacturer"/>
            <span v-if="inputIsRequired">*</span>
        </label>
        <a v-if="policyAttributeWordings.deviceManufacturer.hovertext"
           :data-content=policyAttributeWordings.deviceManufacturer.hovertext
           class="hoverinfo"
           data-container="body"
           data-toggle="popover"
           data-trigger="focus"
           role="button"
           tabindex="0">
            <i class="glyphicon glyphicon-question-sign"></i>
        </a>
        <div class="dropdown-list">
            <v-select
                id="manufacturer"
                :disabled="deviceManufacturer==='Apple'"
                :options="filteredManufacturers"
                :placeholder="lang.selectManufacturer"
                :push-tags=true
                :selectable="(option) => !option.includes(this.productSelector.separator)"
                :taggable=true
                :value="modelSelector.manufacturer.selected"
                class="vs-search"
                @input="setSelectedManufacturer"
                @search="(query) => (modelSelector.manufacturer.search = query)"
            >
                <template #no-options="{}">
                    {{ lang.no_options_found }}
                </template>
                <template #search="{attributes, events}">
                    <input
                        :required="!modelSelector.manufacturer.selected"
                        class="vs__search"
                        v-bind="attributes"
                        v-on="events"
                    />
                </template>
            </v-select>
            <input :value="modelSelector.manufacturer.selected" name="deviceManufacturer" type="hidden">
        </div>
        <div id="model-selector">
            <label for="model">
                <span class="headline" v-text="lang.model"/>
                <span v-if="inputIsRequired">*</span>
            </label>
            <a v-if="policyAttributeWordings.deviceModel.hovertext"
               :data-content=policyAttributeWordings.deviceModel.hovertext
               class="hoverinfo"
               data-container="body"
               data-toggle="popover"
               data-trigger="focus"
               role="button"
               tabindex="0">
                <i class="glyphicon glyphicon-question-sign"></i>
            </a>
            <div class="dropdown-list">
                <v-select
                    id="model"
                    :disabled="!modelSelector.manufacturer.selected"
                    :options="paginated"
                    :placeholder="lang.selectModel"
                    :push-tags=true
                    :taggable=true
                    :value="modelSelector.model.selected"
                    class="vs-search"
                    @input="setSelectedModel"
                    @search="(query) => (modelSelector.model.search = query)"
                >
                    <template #list-footer>
                        <li v-show="hasNextPage" class="loader" v-text="lang.load_more_options"/>
                    </template>
                    <template #no-options="{}">
                        {{ lang.no_options_found }}
                    </template>
                    <template #search="{attributes, events}">
                        <input
                            :required="!modelSelector.model.selected"
                            class="vs__search"
                            v-bind="attributes"
                            v-on="events"
                        />
                    </template>
                </v-select>
                <input :value="modelSelector.model.selected" name="deviceModel" type="hidden">
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: ['productName', 'productSelector', 'policyAttributeWordings', 'lang', 'required', 'connectApiUrl'],
    name: "SmartphoneSelector",

    data: function () {
        return {
            deviceManufacturer: null,
            deviceModel: null,
            firstFetch: true,
            inputIsRequired: false,
            modelSelector: {
                manufacturer: {
                    options: [],
                    selected: '',
                    search: ''
                },
                model: {
                    options: [],
                    selected: null,
                    search: ''
                }
            }
        }
    },

    computed: {
        manufacturerApiUrl() {
            return this.connectApiUrl + '/product-configuration/mobile-devices/manufacturers';
        },
        modelsApiUrl() {
            return this.connectApiUrl + '/product-configuration/mobile-devices/models';
        },
        filteredManufacturers() {
            this.popularManufatures = this.productSelector.popularSmartphoneManufacturers;
            this.separator = this.productSelector.separator;
            this.manufacturerList = this.modelSelector.manufacturer.options.filter(
                (manufacturer) =>
                    manufacturer.toLowerCase().includes(this.modelSelector.manufacturer.search.toLowerCase())
                    &&
                    !this.popularManufatures.includes(manufacturer)
            ).sort();
            return this.popularManufatures.concat([this.separator].concat(this.manufacturerList));
        },
        filteredModels() {
            return this.modelSelector.model.options.filter((model) => {
                if (model) {
                    return model.model.toLowerCase().includes(this.modelSelector.model.search.toLowerCase());
                }
                return false;
            });
        },
        paginated() {
            return this.filteredModels.map(e => e.model);
        },
        hasNextPage() {
            return this.modelSelector.model.lastPageFetched < this.modelSelector.model.totalPages;
        }
    },

    methods: {
        setSelectedManufacturer(selected) {
            if (selected !== this.modelSelector.manufacturer.selected) {
                this.modelSelector.manufacturer.selected = selected;
                this.modelSelector.model.selected = null;
            }
        },
        setSelectedModel(selected) {
            this.modelSelector.model.selected = selected;
        },
        getCategory() {
            if (this.productName.toLowerCase().includes('handy') || this.productName.toLowerCase().includes('smartphone')) {
                return "SMARTPHONE";
            }
            if (this.productName.toLowerCase().includes('tablet')) {
                return "TABLET";
            }
            if (this.productName.toLowerCase().includes('smartwatch')) {
                return "SMARTWATCH";
            }
            return "SMARTPHONE";
        },
        buildModelsUrl() {
            const modelsUrl = new URL(this.modelsApiUrl);
            modelsUrl.searchParams.append('manufacturer', this.modelSelector.manufacturer.selected);
            modelsUrl.searchParams.append('model', this.modelSelector.model.search);
            modelsUrl.searchParams.append('category', this.getCategory());
            modelsUrl.searchParams.append('page', '0');
            modelsUrl.searchParams.append('pageSize', '2000');
            return modelsUrl;
        },
        async loadModels() {
            if (this.modelSelector.manufacturer.selected !== null) {
                if (this.deviceManufacturer && this.firstFetch) {
                    this.modelSelector.manufacturer.selected = this.deviceManufacturer
                    this.firstFetch = false;
                }

                const modelsUrl = this.buildModelsUrl();
                axios.get(modelsUrl.toString()).then((response) => {
                    if (typeof response.data === 'object') {
                        this.modelSelector.model.options = response.data.elements;
                        if (this.deviceModel) {
                            // Preselect Model with the given query parameter
                            let queryModel = decodeURI(this.deviceModel).replaceAll("+", " ");
                            let queryResponseModel = response.data.elements.filter(model => model.model === queryModel);
                            if (queryResponseModel.length > 0) {
                                queryResponseModel[0].label = queryResponseModel[0].model;
                            }
                        }
                    }
                });
            }
        }
    },
    created() {
        this.inputIsRequired = this.required === 'true';
        const urlParams = new URLSearchParams(window.location.search);
        this.deviceManufacturer = urlParams.get('deviceManufacturer');
        this.deviceModel = urlParams.get('deviceModel');
        const manufacturersUrl = new URL(this.manufacturerApiUrl);
        manufacturersUrl.searchParams.append('category', this.getCategory());
        axios.get(manufacturersUrl.toString()).then(response => {

            if (this.deviceManufacturer) {
                // Preselect Manufacturer with the given query parameter
                this.modelSelector.manufacturer.options = response.data;
                this.modelSelector.manufacturer.selected = this.deviceManufacturer;
                this.modelSelector.model.selected = this.deviceModel;
            } else {
                this.modelSelector.manufacturer.options = response.data;
            }

        });
    },

    watch: {
        'modelSelector.manufacturer.selected': function () {
            this.loadModels();
        },
        'modelSelector.model.search': function () {
            this.loadModels();
        }
    },
}

</script>

<style>

</style>
